import { useRef, useCallback, useState, useEffect } from 'react'
import { ICaptchaProviderRef, UseCaptchaReturn, CaptchaHookOptions } from '../types'
import { getCaptchaConfig } from '../services/captchaService'

/**
 * useCaptcha Hook
 * Provides easy-to-use CAPTCHA functionality with automatic configuration
 */
export const useCaptcha = (options: CaptchaHookOptions = {}): UseCaptchaReturn => {
  const {
    autoExecute = false,
    action,
    onVerify,
    onError
  } = options

  // Component ref for CAPTCHA provider
  const captchaRef = useRef<ICaptchaProviderRef>(null)

  // State management
  const [isEnabled, setIsEnabled] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string>()
  const [lastToken, setLastToken] = useState<string | null>(null)

  // Load configuration
  const loadConfig = useCallback(async () => {
    try {
      const config = await getCaptchaConfig()
      console.log('🔒 CAPTCHA config loaded:', { enabled: config.enabled, provider: config.provider })
      setIsEnabled(config.enabled)
      setError(undefined)
    } catch (err: any) {
      console.warn('Failed to load CAPTCHA config:', err.message)
      setError('CAPTCHA configuration error')
      setIsEnabled(false)
    }
  }, [])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  // Refresh configuration function
  const refreshConfig = useCallback(async () => {
    console.log('🔄 Refreshing CAPTCHA configuration...')
    await loadConfig()
  }, [loadConfig])

  // Check if CAPTCHA is ready
  useEffect(() => {
    if (!isEnabled) {
      setIsReady(true)
      return
    }

    const checkReady = () => {
      if (captchaRef.current?.ready) {
        setIsReady(true)
        setError(undefined)

        // Auto-execute if requested
        if (autoExecute) {
          executeCaptcha().catch(err => {
            console.warn('Auto-execute failed:', err.message)
          })
        }
      } else {
        // Check again in 100ms
        setTimeout(checkReady, 100)
      }
    }

    if (isEnabled) {
      checkReady()
    }
  }, [isEnabled, autoExecute])

  /**
   * Execute CAPTCHA and get token
   */
  const executeCaptcha = useCallback(async (): Promise<string | null> => {
    if (!isEnabled) {
      return null
    }

    if (!isReady) {
      const errorMsg = 'CAPTCHA is not ready yet'
      setError(errorMsg)
      onError?.(errorMsg)
      throw new Error(errorMsg)
    }

    if (!captchaRef.current) {
      const errorMsg = 'CAPTCHA provider not available'
      setError(errorMsg)
      onError?.(errorMsg)
      throw new Error(errorMsg)
    }

    try {
      setError(undefined)
      const token = await captchaRef.current.execute(action)

      if (!token) {
        throw new Error('CAPTCHA returned empty token')
      }

      setLastToken(token)
      onVerify?.(token)

      return token
    } catch (err: any) {
      const errorMsg = err.message || 'CAPTCHA execution failed'
      setError(errorMsg)
      onError?.(errorMsg)
      throw err
    }
  }, [isEnabled, isReady, action, onVerify, onError])

  /**
   * Reset CAPTCHA state
   */
  const resetCaptcha = useCallback(() => {
    if (captchaRef.current) {
      captchaRef.current.reset()
    }
    setLastToken(null)
    setError(undefined)
  }, [])

  /**
   * Get CAPTCHA provider name
   */
  const getProviderName = useCallback(() => {
    return 'recaptcha-v3'
  }, [])

  /**
   * Check if last execution was successful
   */
  const wasLastExecutionSuccessful = useCallback(() => {
    return !!lastToken && !error
  }, [lastToken, error])

  return {
    captchaRef,
    executeCaptcha,
    resetCaptcha,
    refreshConfig,
    isEnabled,
    isReady,
    provider: getProviderName(),
    error,
    lastToken,
    wasLastExecutionSuccessful
  }
}

/**
 * Simplified hook for basic CAPTCHA usage
 */
export const useSimpleCaptcha = () => {
  const captcha = useCaptcha()

  const validateForm = useCallback(async (formData: any) => {
    if (!captcha.isEnabled) {
      return formData // Skip CAPTCHA
    }

    try {
      const token = await captcha.executeCaptcha()
      return {
        ...formData,
        captchaToken: token
      }
    } catch (error) {
      throw new Error(`CAPTCHA validation failed: ${error}`)
    }
  }, [captcha])

  return {
    ...captcha,
    validateForm
  }
}

/**
 * Hook for conditional CAPTCHA (only enable for certain conditions)
 */
export const useConditionalCaptcha = (
  condition: boolean,
  options: CaptchaHookOptions = {}
) => {
  const captcha = useCaptcha(options)

  return {
    ...captcha,
    isEnabled: captcha.isEnabled && condition
  }
}
